import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import {
  ColDef,
  ColumnVisibleEvent,
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  GetRowIdParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  ModuleRegistry,
  StatusPanelDef,
  ValueSetterParams,
} from "@ag-grid-community/core";
import "@ag-grid-community/core/dist/styles/ag-grid.css";
import "@ag-grid-community/core/dist/styles/ag-theme-alpine.css";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { AgGridReact } from "@ag-grid-community/react";
import { GridChartsModule } from "@ag-grid-enterprise/charts";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { LicenseManager } from "@ag-grid-enterprise/core";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { MultiFilterModule } from "@ag-grid-enterprise/multi-filter";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { SideBarModule } from "@ag-grid-enterprise/side-bar";
import { StatusBarModule } from "@ag-grid-enterprise/status-bar";
import { Box, Skeleton, Stack, useDisclosure } from "@chakra-ui/react";
import React from "react";
import AutoResizer from "react-virtualized-auto-sizer";
import { IRegistryData } from "../../models/RegistryData";
import {
  IRegistryDataColumn,
  RegistryDataColumnTypeEnum,
} from "../../models/RegistryDataColumn";
import { useAddHiddenColumn } from "../../routes/registries/stores/actions/useAddHiddenColumn";
import { useRemoveHiddenColumn } from "../../routes/registries/stores/actions/useRemoveHiddenColumn";
import { useSelectHiddenColumns } from "../../routes/registries/stores/select/useSelectHiddenColumns";

import { parse } from "date-fns";
import appConfig from "../../app-config";
import { ColumnKeyEnum } from "../../constants";
import { Status } from "../shared/Status";
import { CellCalc } from "./columns/CellCalc";
import { CellFile } from "./columns/CellFile";
import { CellLocked } from "./columns/CellLocked";
import { CellYesNo } from "./columns/CellYesNo";
import { EditorCheckbox } from "./columns/EditorCheckbox";
import { EditorDropdown } from "./columns/EditorDropdown";
import { EditorEmail } from "./columns/EditorEmail";
import { EditorFile } from "./columns/EditorFile";
import { EditorNumber } from "./columns/EditorNumber";
import { EditorRadio } from "./columns/EditorRadio";
import { EditorText } from "./columns/EditorText";
import { EditorYesNo } from "./columns/EditorYesNo";
import "./grid.scss";
import { IndividualModal } from "./IndividualModal";
import { RegistryHeaderModal } from "./RegistryHeaderModal";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  CsvExportModule,
  ExcelExportModule,
  GridChartsModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  SideBarModule,
  RowGroupingModule,
  RangeSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  StatusBarModule,
  ClipboardModule,
  MenuModule,
]);

if (appConfig.AgGrid.license) {
  LicenseManager.setLicenseKey(appConfig.AgGrid.license);
}

const COHORT_HIDE_COLUMNS = [
  ColumnKeyEnum.cohort_individual_id.toString(),
  ColumnKeyEnum.registry_id.toString(),
];

export interface IRegistryColDef extends ColDef {
  meta: IRegistryDataColumn;
}

interface IProps {
  gridRef: any;
  setIsGridReady: () => void;
  data?: IRegistryData;
  isLoading: boolean;
  isError: boolean;
  errorCode?: number;
  errorTitle?: string;
  errorDescription?: string;
  isCohort?: boolean;
  isRegistry?: boolean;
  registryId?: string;
  cohortId?: string;
}

const CoreDataGridContainer = ({
  data,
  gridRef,
  setIsGridReady,
  isLoading,
  isError,
  errorCode,
  errorTitle,
  errorDescription,
  isCohort = false,
  isRegistry = false,
  cohortId,
  registryId,
}: IProps) => {
  const [rowData, setRowData] = React.useState<any>(); // Set rowData to Array of Objects, one Object per Row
  const [viewIndividualId, set_viewIndividualId] = React.useState<string>();
  const [viewIndividualRegistryId, set_viewIndividualRegistryId] =
    React.useState<string>();
  const [columnMeta, setColumnMeta] = React.useState<IRegistryDataColumn>();
  const [isRefreshGrid, setIsRefreshGrid] = React.useState<boolean>(false);

  const {
    isOpen: isOpenHeaderModal,
    onOpen: onOpenHeaderModal,
    onClose: onCloseHeaderModal,
  } = useDisclosure();

  const {
    isOpen: isOpenIndividualModal,
    onOpen: onOpenIndividualModal,
    onClose: onCloseIndividualModal,
  } = useDisclosure();

  const hiddenColumns = useSelectHiddenColumns();

  const addHiddenColumn = useAddHiddenColumn();
  const removeHiddenColumn = useRemoveHiddenColumn();

  // Each Column Definition results in one Column.
  const [columnDefs, setColumnDefs] = React.useState<any>([]);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = React.useMemo(
    () => ({
      sortable: true,
    }),
    []
  );

  const columnTypes = React.useMemo(
    () => ({
      number: {},
    }),
    []
  );

  const handleHeaderDetails = React.useCallback(
    (meta: IRegistryDataColumn) => {
      setColumnMeta(meta);
      onOpenHeaderModal();
    },
    [onOpenHeaderModal]
  );

  // HELPER FOR DATE COMPARISON
  const _monthToNum = React.useCallback((date) => {
    if (date === undefined || date === null) {
      return null;
    }

    // Split yyyy-mm-dd from hh:mm:ss
    let datePartsYYYYMMDD = date.split(" ");
    if (!datePartsYYYYMMDD) {
      return null;
    }
    let dateParts = datePartsYYYYMMDD[0].split("-");
    let yearNumber = Number(dateParts[0]);
    let monthNumber = Number(dateParts[1]);
    let dayNumber = Number(dateParts[2]);

    let result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
    return result;
  }, []);

  // DATE COMPARATOR FOR SORTING
  const dateComparator = React.useCallback(
    (date1, date2) => {
      let date1Number = _monthToNum(date1);
      let date2Number = _monthToNum(date2);

      if (date1Number === null && date2Number === null) {
        return 0;
      }
      if (date1Number === null) {
        return -1;
      }
      if (date2Number === null) {
        return 1;
      }

      return date1Number - date2Number;
    },
    [_monthToNum]
  );

  const handleIndividualDetails = React.useCallback(
    (individualId: string, individualRegistryIdUpdate: string) => {
      if (individualId && individualRegistryIdUpdate) {
        set_viewIndividualId(individualId);
        set_viewIndividualRegistryId(individualRegistryIdUpdate);
        onOpenIndividualModal();
      }
    },
    [onOpenIndividualModal]
  );

  const handleOnCloseHeaderModal = () => {
    setColumnMeta(undefined);
    onCloseHeaderModal();
  };

  const handleOnCloseIndividualModal = () => {
    set_viewIndividualId(undefined);
    onCloseIndividualModal();
  };

  const onColumnVisible = React.useCallback(
    (event: ColumnVisibleEvent) => {
      const colField = event?.column?.getColDef().field;

      if (colField) {
        if (event.visible) {
          removeHiddenColumn({ columnName: colField });
        } else {
          addHiddenColumn({ columnName: colField });
        }
      }
    },
    [removeHiddenColumn, addHiddenColumn]
  );

  const getContextMenuItems = React.useCallback(
    (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      var result: (string | MenuItemDef)[] = [
        "separator",
        "autoSizeAll",
        "expandAll",
        "contractAll",
        "separator",
        "copy",
        "copyWithHeaders",
        "paste",
        "separator",
        "resetColumns",
        "separator",
        "export",
        "separator",
        "chartRange",
      ];

      result.unshift({
        name: "View Column Definition",
        action: () => {
          const colDef = params.column?.getColDef() as IRegistryColDef;

          if (colDef?.meta) {
            handleHeaderDetails(colDef?.meta);
          }
        },
      });

      result.unshift({
        name: "View Individual",
        action: () => {
          const individualIdUpdate = params.node?.data[ColumnKeyEnum.record_id];

          let individualRegistryIdUpdate =
            params.node?.data[ColumnKeyEnum.registry_id];

          if (isRegistry && registryId) {
            individualRegistryIdUpdate = registryId;
          }

          handleIndividualDetails(
            individualIdUpdate,
            individualRegistryIdUpdate
          );
        },
      });

      return result;
    },
    [handleIndividualDetails, handleHeaderDetails, registryId, isRegistry]
  );

  const getRowId = React.useCallback(
    (params: GetRowIdParams) => {
      let rowId = params.data[ColumnKeyEnum.record_id];

      if (isCohort) {
        rowId = params.data[ColumnKeyEnum.cohort_individual_id];
      }

      return rowId;
    },
    [isCohort]
  );

  const getMainMenuItems = React.useCallback(
    (params: GetMainMenuItemsParams): (string | MenuItemDef)[] => {
      var result: (string | MenuItemDef)[];

      //Customizes 'Group' column menu
      if (params.column.getId() == "ag-Grid-AutoColumn") {
        result = [
          "pinSubMenu",
          "autoSizeThis",
          "autoSizeAll",
          "separator",
          "expandAll",
          "contractAll",
          "separator",
          "resetColumns",
        ];
      } else {
        if (params.column.isRowGroupActive()) {
          result = [
            "separator",
            "pinSubMenu",
            "autoSizeThis",
            "autoSizeAll",
            "separator",
            "valueAggSubMenu",
            "separator",
            "rowUnGroup",
            "expandAll",
            "contractAll",
            "separator",
            "resetColumns",
          ];
        } else {
          result = [
            "separator",
            "pinSubMenu",
            "autoSizeThis",
            "autoSizeAll",
            "separator",
            "valueAggSubMenu",
            "separator",
            "rowGroup",
            "expandAll",
            "contractAll",
            "separator",
            "resetColumns",
          ];
        }
        result.unshift({
          name: "View Column Definition",
          action: () => {
            const colDef = params.column?.getColDef() as IRegistryColDef;

            if (colDef?.meta) {
              handleHeaderDetails(colDef?.meta);
            }
          },
        });
      }

      return result;
    },
    [handleHeaderDetails]
  );

  // Example load data from sever
  React.useMemo(() => {
    if (data) {
      setRowData(data.rows);

      if (data.columns) {
        const newColumns: any[] = [];

        data.columns.forEach((columnMeta) => {
          let newCol: IRegistryColDef = {
            headerName: columnMeta.display,
            field: columnMeta.column_key,
            headerTooltip: columnMeta.help_text,
            tooltipComponentParams: {
              tooltipShowDelay: 0,
            },
            filter: "agMultiColumnFilter",
            filterParams: {
              buttons: ["clear"],
              meta: columnMeta,
              filters: [
                {
                  filter: "agTextColumnFilter",
                },
                {
                  filter: "agSetColumnFilter",
                },
              ],
            },
            resizable: true,
            cellEditor: EditorText,
            editable: false,
            meta: columnMeta,
            menuTabs: ["filterMenuTab", "generalMenuTab"],
            cellRendererParams: {
              meta: columnMeta,
            },
            enableRowGroup: true,
            enablePivot: true,
            enableValue: true,
            allowedAggFuncs: ["count", "first", "last"],
            chartDataType: "series",
            aggFunc: "count",
          };

          // Add checkbox
          // TODO: hold this in the column meta
          // https://app.clickup.com/t/3wx23pc
          if (columnMeta.column_key === ColumnKeyEnum.record_id) {
            newCol = {
              ...newCol,
              rowDrag: true,
              checkboxSelection: true,
              headerCheckboxSelection: true,
              headerCheckboxSelectionFilteredOnly: true,
              pinned: "left",
              lockVisible: true,
              lockPosition: true,
              suppressMovable: true,
            };
          }

          // Configure col for different column types
          if (columnMeta.type === RegistryDataColumnTypeEnum.number) {
            newCol.type = "number";
            newCol.cellEditor = EditorNumber;
            newCol.filter = "agNumberColumnFilter";
            newCol.allowedAggFuncs = ["sum", "avg", "min", "max"]; //adjusting available agg functions
            newCol.aggFunc = "avg";
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.email) {
            newCol.cellEditor = EditorEmail;
          } else if (
            // TODO: do we need validation thing here?
            columnMeta.validation === "file" ||
            columnMeta.type === RegistryDataColumnTypeEnum.file
          ) {
            newCol.cellEditor = EditorFile;
            newCol.cellRenderer = CellFile;
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.radio) {
            // newCol.cellRenderer = CellRadio;
            // newCol.filter = FilterRadio;
            newCol.cellEditor = EditorRadio;
            newCol.chartDataType = "category";
            newCol.cellEditorPopup = true;
            newCol.valueGetter = (params) => {
              // Convert all incoming values to target labels for display
              const colDef = params.colDef as any;
              const colMeta = colDef.meta as IRegistryDataColumn;
              let colValue = "";
              if (params.data) {
                colValue = params.data[colDef.field];
              }

              if (colMeta.options && colMeta.options[colValue]) {
                colValue = colMeta.options[colValue].label;
              }
              return colValue;
            };
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.dropdown) {
            // newCol.cellRenderer = CellDropdown;
            // newCol.filter = FilterDropdown;
            newCol.cellEditor = EditorDropdown;
            newCol.cellEditorPopup = true;
            newCol.chartDataType = "category";
            newCol.valueGetter = (params) => {
              // Convert all incoming values to target labels for display
              const colDef = params.colDef as any;
              const colMeta = colDef.meta as IRegistryDataColumn;
              let colValue = "";
              if (params.data) {
                colValue = params.data[colDef.field];
              }
              if (colMeta.options && colMeta.options[colValue]) {
                colValue = colMeta.options[colValue].label;
              }
              return colValue;
            };
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.yesno) {
            newCol.cellRenderer = CellYesNo;
            newCol.cellEditor = EditorYesNo;
            // newCol.filter = FilterYesNo;
            newCol.cellEditorPopup = true;
            newCol.chartDataType = "category";
            newCol.valueGetter = (params) => {
              // Convert all incoming values to target labels for display
              const colDef = params.colDef as any;

              let colValue: string | number = "";
              if (params.data) {
                colValue = params.data[colDef.field];
              }

              if (colValue === "0" || colValue === 0) {
                colValue = "no";
              } else if (colValue === "1" || colValue === 1) {
                colValue = "yes";
              }

              return colValue;
            };
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.checkbox) {
            newCol.cellEditor = EditorCheckbox;
            newCol.sortable = false;
            newCol.cellEditorPopup = true;
            newCol.chartDataType = "category";
            newCol.valueGetter = (params) => {
              // Convert all incoming values to target labels for display
              const colDef = params.colDef as any;
              const colMeta = colDef.meta as IRegistryDataColumn;

              let colValue: string[] = [];

              if (params.data && colMeta?.options) {
                const checkboxOptions = colMeta.options;

                Object.entries(checkboxOptions).forEach(
                  ([value, option], idx) => {
                    if (option && option.lookup_key) {
                      try {
                        if (params.data[option.lookup_key] === 1) {
                          colValue.push(option.label);
                        }
                      } catch (e) {}
                    }
                  }
                );
              }

              return colValue.join(", ");
            };
            newCol.valueSetter = (params: ValueSetterParams) => {
              // Update the columns that support this checkbox
              const rowNode = params.node;
              const colDef = params.colDef as any;
              const colMeta = colDef.meta as IRegistryDataColumn;
              // Get the new labels for the incoming change
              const newValueLabels: string = params.newValue;

              // Iterate over the checkbox options and see if the option
              // is included int he new values labels
              const checkboxOptions = colMeta.options;
              if (rowNode) {
                // Update lookup key
                if (checkboxOptions) {
                  Object.entries(checkboxOptions).forEach(([key, option]) => {
                    if (option && option.lookup_key) {
                      let updateToValue = 0;
                      if (
                        newValueLabels &&
                        newValueLabels.includes(option.label)
                      ) {
                        updateToValue = 1;
                      }

                      params.data[option.lookup_key] = updateToValue;
                    }
                  });
                }

                params.api.refreshCells({ rowNodes: [rowNode] });
              }

              return true;
            };
          } else if (
            columnMeta.type === RegistryDataColumnTypeEnum.checkbox_option
          ) {
            newCol.hide = columnMeta.is_default_hidden;
            newCol.chartDataType = "category";
            newCol.aggFunc = "count";

            if (!columnMeta.is_editable) {
              newCol.editable = false;
              newCol.cellRenderer = CellLocked;
              newCol.meta = { ...columnMeta, is_never_edit: true };
            }
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.notes) {
            newCol.cellEditor = "agLargeTextCellEditor";
            newCol.filter = "agTextColumnFilter";
            newCol.cellEditorPopup = true;
          } else if (columnMeta.type === RegistryDataColumnTypeEnum.calc) {
            newCol.cellRenderer = CellCalc;
            newCol.chartDataType = "series";
            newCol.type = "number";
            newCol.allowedAggFuncs = ["sum", "min", "max", "avg", "count"];
          }

          // TODO: Date type
          if (columnMeta.column_key === ColumnKeyEnum.created_date) {
            newCol.field = ColumnKeyEnum.created_date;
            newCol.comparator = dateComparator;
            newCol.filter = "agDateColumnFilter";
            newCol.filterParams = {
              debounceMs: 500,
              // suppressAndOrCondition: true,
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                if (cellValue === null) {
                  return 0;
                }

                const cellDate = parse(
                  cellValue,
                  "yyyy-MM-dd HH:mm:ss",
                  new Date()
                );

                if (cellDate < filterLocalDateAtMidnight) {
                  return -1;
                } else if (cellDate > filterLocalDateAtMidnight) {
                  return 1;
                } else {
                  return 0;
                }
              },
            };
          }

          if (isCohort && COHORT_HIDE_COLUMNS.includes(columnMeta.column_key)) {
            newCol.hide = true;
          }

          newColumns.push(newCol);
        });

        setColumnDefs(newColumns);
      }
    }
  }, [data, dateComparator, isCohort]);

  const statusBar = React.useMemo<{
    statusPanels: StatusPanelDef[];
  }>(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
        { statusPanel: "agFilteredRowCountComponent" },
        { statusPanel: "agSelectedRowCountComponent" },
        { statusPanel: "agAggregationComponent" },
      ],
    };
  }, []);

  const sideBar = React.useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: false,
            suppressValues: false,
            suppressPivots: false,
            suppressPivotMode: false,
            // suppressColumnFilter: true,
            // suppressColumnSelectAll: true,
            // suppressColumnExpandAll: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            // suppressExpandAll: true,
            // suppressFilterSearch: true,
          },
        },
      ],
      hiddenByDefault: true,
      // defaultToolPanel: "filters",
    };
  }, []);

  // access API from event object
  const onGridReady = React.useCallback(
    (e: GridReadyEvent) => {
      setIsGridReady();
      e.columnApi.setColumnsVisible(hiddenColumns, false);
    },
    [setIsGridReady, hiddenColumns]
  );

  const gridOptions: GridOptions = {
    onGridReady: onGridReady,
    columnDefs: columnDefs,
    rowData: rowData,
    sideBar: sideBar,
    enableCellChangeFlash: true,
    statusBar: statusBar,
    defaultColDef: defaultColDef,
    animateRows: true,
    rowSelection: "multiple",
    onColumnVisible: onColumnVisible,
    undoRedoCellEditing: true,
    singleClickEdit: true,
    undoRedoCellEditingLimit: 20,
    enableRangeSelection: true,
    getContextMenuItems: getContextMenuItems,
    getMainMenuItems: getMainMenuItems,
    getRowId: getRowId,
    columnTypes: columnTypes,
    rowDragManaged: true,
    enableCharts: true,
    rowDragMultiRow: true,
    pivotMode: false,
    rowGroupPanelShow: "always",
    suppressPropertyNamesCheck: true, // let us put custom meta on the col def,
    customChartThemes: {
      geneial: {
        baseTheme: "ag-vivid",
        palette: {
          fills: ["#00ACC1", "#386968", "#2B7781", "#053D3B"],
          strokes: ["#1F94A3", "#1F5755", "#2B7781", "#053635"],
        },
      },
    },
    chartThemes: ["geneial"],
    chartThemeOverrides: {
      common: {
        padding: {
          top: 20,
          right: 40,
          bottom: 20,
          left: 30,
        },
      },
      cartesian: {
        axes: {
          category: {
            label: {
              rotation: 0,
            },
          },
        },
      },
    },
  };

  React.useEffect(() => {
    // When switcigin
    setIsRefreshGrid(true);
    setTimeout(() => {
      setIsRefreshGrid(false);
    }, 0);
  }, [registryId, cohortId]);

  return (
    <>
      <Stack spacing={0} h="full" w="full" position={"relative"}>
        <Box flex={1}>
          {(isLoading || isRefreshGrid) && (
            <Stack spacing={6}>
              <Skeleton height="5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
              <Skeleton height="2.5rem" />
            </Stack>
          )}

          {!isLoading && !isError && !isRefreshGrid && (
            <AutoResizer>
              {({ height, width }) => {
                return (
                  <Box
                    className="ag-theme-alpine"
                    style={{ width: width, height: height }}
                  >
                    <AgGridReact
                      ref={gridRef} // Ref for accessing Grid's API
                      {...gridOptions}
                    />
                  </Box>
                );
              }}
            </AutoResizer>
          )}

          {isError && (
            <Box>
              {errorCode === 404 ? (
                <Status.NotFound
                  title={errorTitle}
                  description={errorDescription}
                />
              ) : errorCode === 503 ? (
                <Status.IsError title={errorDescription} />
              ) : (
                <Status.IsError title="Failed to load data" />
              )}
            </Box>
          )}
        </Box>
      </Stack>
      {columnMeta && (
        <RegistryHeaderModal
          isOpen={isOpenHeaderModal}
          onClose={handleOnCloseHeaderModal}
          column={columnMeta}
        />
      )}
      {viewIndividualId && viewIndividualRegistryId && (
        <IndividualModal
          isOpen={isOpenIndividualModal}
          onClose={handleOnCloseIndividualModal}
          individualId={viewIndividualId}
          registryId={viewIndividualRegistryId}
        />
      )}
    </>
  );
};

export default CoreDataGridContainer;
